import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sun, Battery, Activity, ShieldCheck } from "lucide-react";
import SEO from "@/components/SEO";
import {
  MarketingHeader, MarketingFooter, MarketingBackdrop,
} from "@/components/MarketingShell";

/**
 * /solar — dedicated indexable landing for the Solar service line.
 *
 * Only mentions capabilities we actually deliver today (hardware confirmed
 * from the live inventory catalogue): rooftop PV, hybrid inverters, LFP
 * battery storage, monitoring. No fabricated stats.
 */
export default function SolarPage() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Solar power installation",
    "name": "Solar power &amp; battery storage",
    "provider": { "@id": "https://www.powerbuilt.sx/#org" },
    "areaServed": [
      { "@type": "Country", "name": "Sint Maarten" },
      { "@type": "Country", "name": "Saint Martin" },
    ],
    "description": "Rooftop solar PV, hybrid inverters and LFP battery storage designed for the Caribbean climate. Consultation, installation, monitoring and handover — end-to-end from a single PowerBuilt account.",
    "url": "https://www.powerbuilt.sx/solar",
  };
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home",  "item": "https://www.powerbuilt.sx/" },
      { "@type": "ListItem", "position": 2, "name": "Solar", "item": "https://www.powerbuilt.sx/solar" },
    ],
  };
  return (
    <MarketingBackdrop>
      <SEO
        path="/solar"
        title="Solar Power & Battery Storage in Sint Maarten — PowerBuilt"
        description="Rooftop solar PV, hybrid inverters and LFP battery storage for St. Maarten homes and businesses. Consultation, installation and monitoring — end-to-end from PowerBuilt."
        jsonLd={{ "@context": "https://schema.org", "@graph": [jsonLd, breadcrumb] }}
      />
      <MarketingHeader />

      <main className="relative z-10 max-w-6xl mx-auto px-6 pt-8 md:pt-16 pb-16">
        <nav aria-label="Breadcrumb" className="text-xs text-white/50 mb-6">
          <Link to="/" className="hover:text-white">Home</Link> <span className="mx-1">/</span> <span className="text-white/70">Solar</span>
        </nav>

        <section className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="overline text-[#C9A356] mb-4">Service · Solar</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-medium tracking-tight leading-[1.05]">
              Solar power, designed for the <span className="text-[#B7F58A]">Caribbean</span>.
            </h1>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-lg">
              PowerBuilt designs and installs rooftop solar and battery systems for homes and businesses across Sint Maarten &mdash; sized to your usage, priced up-front, and monitored after handover.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-accent text-base px-7 py-3" data-testid="solar-cta-quote">
                Request a solar quote <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <Link to="/register" className="px-7 py-3 rounded-md font-medium text-white border-2 border-white/30 hover:bg-white/10 transition">
                Open a customer account
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-6 bg-[#8CDC5A]/10 blur-2xl rounded-[2.5rem] pointer-events-none"></div>
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-black/40">
              <img
                src="/landing-hero.jpg"
                alt="PowerBuilt rooftop solar installation on a Caribbean villa"
                loading="eager"
                width="960" height="1200"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        <section className="mt-20 border-t border-white/10 pt-12">
          <p className="overline text-[#C9A356] mb-3">What we install</p>
          <h2 className="font-heading text-3xl md:text-4xl leading-tight max-w-2xl">
            A complete solar system, not just panels.
          </h2>
          <div className="grid md:grid-cols-3 gap-5 mt-8">
            {[
              { icon: Sun,      title: "Solar generation",   copy: "High-efficiency solar modules mounted on marine-grade aluminium rails and stainless roof hooks — engineered for Caribbean wind loading." },
              { icon: Battery,  title: "Battery storage",    copy: "Hybrid inverters paired with LFP batteries (Pylontech, BYD) so your home keeps running when the grid drops." },
              { icon: Activity, title: "Monitoring",         copy: "Every installation ships with app-level monitoring so you and PowerBuilt see production, consumption and battery state." },
            ].map((f) => (
              <article key={f.title} className="group rounded-2xl border border-white/10 bg-white/[0.03] p-7 hover:bg-white/[0.05] transition hover:border-[#C9A356]/50">
                <div className="w-11 h-11 rounded-xl grid place-items-center mb-5 bg-[#C9A356]/15 text-[#C9A356] group-hover:bg-[#C9A356]/25 transition">
                  <f.icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading text-xl mb-2">{f.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{f.copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mt-20 border-t border-white/10 pt-12">
          <p className="overline text-[#C9A356] mb-3">How it works</p>
          <h2 className="font-heading text-3xl md:text-4xl leading-tight max-w-2xl">
            From first call to first kWh.
          </h2>
          <ol className="mt-8 grid md:grid-cols-3 gap-5 list-decimal list-inside marker:text-[#C9A356]">
            <li className="rounded-2xl border border-white/10 bg-white/[0.03] p-7">
              <h3 className="font-heading text-lg mt-2 mb-2">Consultation &amp; sizing</h3>
              <p className="text-sm text-slate-400 leading-relaxed">Site survey, roof assessment and a system sized to your actual electric usage — not a template.</p>
            </li>
            <li className="rounded-2xl border border-white/10 bg-white/[0.03] p-7">
              <h3 className="font-heading text-lg mt-2 mb-2">Design &amp; installation</h3>
              <p className="text-sm text-slate-400 leading-relaxed">Engineering, permits, materials procurement and licensed installation, coordinated through one PowerBuilt project number.</p>
            </li>
            <li className="rounded-2xl border border-white/10 bg-white/[0.03] p-7">
              <h3 className="font-heading text-lg mt-2 mb-2">Commissioning &amp; handover</h3>
              <p className="text-sm text-slate-400 leading-relaxed">Grid-export test, monitoring set-up, signed handoff certificate, and ongoing <Link to="/maintenance" className="underline decoration-[#C9A356]/50 hover:decoration-[#C9A356]">system maintenance</Link>.</p>
            </li>
          </ol>
        </section>

        <section className="mt-20 border-t border-white/10 pt-12 grid md:grid-cols-3 gap-6 items-start">
          <div className="md:col-span-2">
            <p className="overline text-[#C9A356] mb-3">Related</p>
            <h2 className="font-heading text-2xl md:text-3xl leading-tight">Everything else your solar system needs.</h2>
            <p className="text-slate-400 mt-3 max-w-xl">Solar rarely lives alone. PowerBuilt handles the electrical side and looks after the system after handover.</p>
            <div className="mt-6 flex flex-wrap gap-4">
              <Link to="/electrical"  className="px-5 py-2 rounded-md border border-white/20 text-white/85 hover:bg-white/10 transition">Electrical work &rarr;</Link>
              <Link to="/maintenance" className="px-5 py-2 rounded-md border border-white/20 text-white/85 hover:bg-white/10 transition">System maintenance &rarr;</Link>
              <Link to="/impact"      className="px-5 py-2 rounded-md border border-white/20 text-white/85 hover:bg-white/10 transition">Our sustainability impact &rarr;</Link>
            </div>
          </div>
          <div className="rounded-2xl border border-[#C9A356]/25 bg-[#C9A356]/5 p-6">
            <ShieldCheck className="w-6 h-6 text-[#C9A356] mb-3" />
            <h2 className="font-heading text-lg mb-2">Backed by a written handoff</h2>
            <p className="text-sm text-slate-300 leading-relaxed">Every install closes with a signed PowerBuilt Certificate of Handoff — commissioning results, equipment list, warranty terms.</p>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </MarketingBackdrop>
  );
}
